import type {
  FolderColumnRef,
  FolderFileKind,
  FolderFilePlan,
  FolderPlanResponse,
  FolderSheetPlan,
  FolderSheetRef,
} from './api';

export function refLabel(r: FolderSheetRef): string {
  return r.sheet === 'Sheet1' ? r.file : `${r.file} · ${r.sheet}`;
}

/** The batch job names a sheet item "file :: sheet" and a file item by its staged name. */
export function sheetLoadKey(r: FolderSheetRef): string {
  return `${r.file} :: ${r.sheet}`;
}

export function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function count(n: number, noun: string): string {
  return `${n.toLocaleString()} ${noun}${n === 1 ? '' : 's'}`;
}

const DUPLICATE_PREFIX = 'duplicate_of:';

/** The corpus profiler's skip reason codes, in words. */
export function describeSkipReason(reason: string | null): string {
  if (reason === null || reason === '') return 'The analysis gave no reason.';
  if (reason === 'empty') return 'It has no data rows.';
  if (reason === 'banner_no_header') {
    return 'Most of its column headers are blank, which usually means a title banner sits where the header row should be.';
  }
  if (reason.startsWith(DUPLICATE_PREFIX)) {
    return `It repeats ${reason.slice(DUPLICATE_PREFIX.length)}.`;
  }
  return reason;
}

/** One file of the analyzed folder with everything the plan says about it. */
export interface FolderFileRow {
  relative: string;
  /** The plan entry for a graph, native, document or other file, or for a
   *  redundant file of any kind. */
  file: FolderFilePlan | null;
  /** The file's parsed sheets or tables, in plan order. */
  sheets: FolderSheetPlan[];
  /** Why the file could not be read, when it could not. */
  unreadable: string | null;
}

export function buildFolderRows(
  plan: Pick<FolderPlanResponse, 'sheets' | 'files' | 'unreadable'>,
): FolderFileRow[] {
  const rows = new Map<string, FolderFileRow>();
  const row = (relative: string): FolderFileRow => {
    let r = rows.get(relative);
    if (!r) {
      r = { relative, file: null, sheets: [], unreadable: null };
      rows.set(relative, r);
    }
    return r;
  };
  for (const s of plan.sheets) row(s.sheet.file).sheets.push(s);
  for (const f of plan.files) row(f.relative).file = f;
  for (const [relative, why] of plan.unreadable) row(relative).unreadable = why;
  return [...rows.values()].sort((a, b) =>
    a.relative.localeCompare(b.relative, undefined, { numeric: true, sensitivity: 'base' }),
  );
}

export function describeSheet(s: FolderSheetPlan): string {
  return `${count(s.rows, 'row')}, ${count(s.columns.length, 'column')}`;
}

const WORKBOOK_EXT = /\.(xlsx|xls|ods)$/i;

export function describeRow(row: FolderFileRow): string {
  if (row.unreadable !== null) return 'A spreadsheet TReS could not read';
  const only = row.sheets.length === 1 ? row.sheets[0] : undefined;
  if (only) {
    return only.sheet.sheet === 'Sheet1'
      ? `Table, ${describeSheet(only)}`
      : `Sheet “${only.sheet.sheet}”, ${describeSheet(only)}`;
  }
  if (row.sheets.length > 1) {
    return WORKBOOK_EXT.test(row.relative)
      ? `Workbook, ${count(row.sheets.length, 'sheet')}`
      : count(row.sheets.length, 'table');
  }
  const f = row.file;
  if (!f) return '';
  const content = f.content ? `, ${f.content}` : '';
  switch (f.kind) {
    case 'graph':
      return `Graph file${content}`;
    case 'native':
      return `Native QROS file${content}${f.statements != null ? `, ${count(f.statements, 'statement')}` : ''}`;
    case 'document':
      return 'Document';
    case 'sheet':
      return 'Spreadsheet';
    case 'other':
      return 'Not an importable format';
  }
}

/**
 * Everything the analysis says about one file. Problems carry a resolution in
 * the report; information needs none.
 */
export type FolderFinding =
  | { kind: 'unreadable'; relative: string; why: string }
  | { kind: 'load-failed'; key: string; sheet: FolderSheetRef | null; message: string }
  | {
      kind: 'redundant-file';
      relative: string;
      fileKind: FolderFileKind;
      duplicateOf: string | null;
      supersededBy: string | null;
    }
  | { kind: 'sheet-skip-proposed'; sheet: FolderSheetRef; reason: string | null }
  | {
      kind: 'event-grain';
      sheet: FolderSheetRef;
      subjectColumn: string;
      qualifierColumns: string[];
      repeatedSubjects: number;
    }
  | { kind: 'missing-bridge'; here: FolderColumnRef; other: FolderColumnRef }
  | { kind: 'document-index'; relative: string; note: string }
  | { kind: 'not-importable'; relative: string }
  | { kind: 'reader-notes'; sheet: FolderSheetRef; notes: string[] }
  | {
      kind: 'identity-join';
      here: FolderColumnRef;
      other: FolderColumnRef;
      overlap: number;
      coverage: number;
    }
  | {
      kind: 'sparse-column';
      column: FolderColumnRef;
      nonBlankRatio: number;
      applicabilityColumn: string | null;
      appliesWhen: string[];
    };

const PROBLEM_KINDS: ReadonlySet<FolderFinding['kind']> = new Set([
  'unreadable',
  'load-failed',
  'redundant-file',
  'sheet-skip-proposed',
  'event-grain',
  'missing-bridge',
]);

export function isProblem(f: FolderFinding): boolean {
  return PROBLEM_KINDS.has(f.kind);
}

export interface FileFindings {
  problems: FolderFinding[];
  information: FolderFinding[];
}

/**
 * Group the analysis by file. A finding that involves two files (a missing
 * bridge, an identity join) is listed under each file involved, once per file.
 * `failures` maps a load key (see `sheetLoadKey`) to the load's error message.
 */
export function groupFindingsByFile(
  plan: Pick<FolderPlanResponse, 'sheets' | 'files' | 'corpus' | 'unreadable'>,
  failures: Readonly<Record<string, string>>,
): Map<string, FileFindings> {
  const out = new Map<string, FileFindings>();
  const add = (file: string, finding: FolderFinding) => {
    let g = out.get(file);
    if (!g) {
      g = { problems: [], information: [] };
      out.set(file, g);
    }
    (isProblem(finding) ? g.problems : g.information).push(finding);
  };

  for (const [relative, why] of plan.unreadable) add(relative, { kind: 'unreadable', relative, why });
  for (const f of plan.files) {
    const message = failures[f.relative];
    if (message !== undefined) {
      add(f.relative, { kind: 'load-failed', key: f.relative, sheet: null, message });
    }
  }
  for (const s of plan.sheets) {
    const key = sheetLoadKey(s.sheet);
    const message = failures[key];
    if (message !== undefined) {
      add(s.sheet.file, { kind: 'load-failed', key, sheet: s.sheet, message });
    }
  }
  for (const f of plan.files) {
    if (f.kind === 'other') {
      add(f.relative, { kind: 'not-importable', relative: f.relative });
    } else if (f.duplicate_of || f.superseded_by) {
      add(f.relative, {
        kind: 'redundant-file',
        relative: f.relative,
        fileKind: f.kind,
        duplicateOf: f.duplicate_of ?? null,
        supersededBy: f.superseded_by ?? null,
      });
    } else if (f.kind === 'document') {
      add(f.relative, { kind: 'document-index', relative: f.relative, note: f.note });
    }
  }
  for (const s of plan.sheets) {
    if (s.disposition === 'skip') {
      add(s.sheet.file, { kind: 'sheet-skip-proposed', sheet: s.sheet, reason: s.skip_reason });
    }
  }
  for (const e of plan.corpus.event_grain) {
    add(e.sheet.file, {
      kind: 'event-grain',
      sheet: e.sheet,
      subjectColumn: e.subject_column,
      qualifierColumns: e.qualifier_columns,
      repeatedSubjects: e.repeated_subjects,
    });
  }
  for (const b of plan.corpus.missing_bridges) {
    add(b.world_a.sheet.file, { kind: 'missing-bridge', here: b.world_a, other: b.world_b });
    if (b.world_b.sheet.file !== b.world_a.sheet.file) {
      add(b.world_b.sheet.file, { kind: 'missing-bridge', here: b.world_b, other: b.world_a });
    }
  }
  for (const s of plan.sheets) {
    if (s.notes && s.notes.length > 0) {
      add(s.sheet.file, { kind: 'reader-notes', sheet: s.sheet, notes: s.notes });
    }
  }
  for (const c of plan.corpus.identity_candidates) {
    const coverage = Math.max(c.containment_a_in_b, c.containment_b_in_a);
    add(c.a.sheet.file, { kind: 'identity-join', here: c.a, other: c.b, overlap: c.overlap, coverage });
    if (c.b.sheet.file !== c.a.sheet.file) {
      add(c.b.sheet.file, { kind: 'identity-join', here: c.b, other: c.a, overlap: c.overlap, coverage });
    }
  }
  for (const s of plan.corpus.sparse_columns) {
    add(s.column.sheet.file, {
      kind: 'sparse-column',
      column: s.column,
      nonBlankRatio: s.non_blank_ratio,
      applicabilityColumn: s.applicability_column,
      appliesWhen: s.applies_when,
    });
  }
  return out;
}

export function countFindings(groups: Iterable<FileFindings>): {
  problems: number;
  information: number;
} {
  let problems = 0;
  let information = 0;
  for (const g of groups) {
    problems += g.problems.length;
    information += g.information.length;
  }
  return { problems, information };
}
